import { useState, useEffect } from 'react';
import { AssetRegistrySidebar, type AssetRegistryView } from '../../components/assetRegistry/AssetRegistrySidebar';
import { AssetLibrary } from './AssetLibrary';
import { AssetDetail } from './AssetDetail';
import { AssetForm } from './AssetForm';
import { LogUsageModal } from './LogUsageModal';
import { ChannelCheck } from './ChannelCheck';
import { ReviewQueue } from './ReviewQueue';
import { supabase } from '../../lib/supabase';
import type { Asset } from '../../types/assetRegistry';

type PanelState =
  | { kind: 'none' }
  | { kind: 'detail'; asset: Asset }
  | { kind: 'form'; asset: Asset | null }
  | { kind: 'log'; asset: Asset };

export default function AssetRegistry() {
  const [currentView, setCurrentView] = useState<AssetRegistryView>('library');
  const [panel, setPanel] = useState<PanelState>({ kind: 'none' });
  const [refreshKey, setRefreshKey] = useState(0);
  const [queueCount, setQueueCount] = useState(0);

  useEffect(() => {
    async function fetchQueueCount() {
      const { count } = await supabase
        .from('assets')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'draft')
        .eq('source', 'agent');
      setQueueCount(count ?? 0);
    }
    fetchQueueCount();
  }, []);

  function handleNavigate(view: AssetRegistryView) {
    setCurrentView(view === 'add' ? 'library' : view);
    if (view === 'add') setPanel({ kind: 'form', asset: null });
    else setPanel({ kind: 'none' });
  }

  function handleSelectAsset(asset: Asset) { setPanel({ kind: 'detail', asset }); }
  function handleEditAsset(asset: Asset) { setPanel({ kind: 'form', asset }); }
  function handleLogUsage(asset: Asset) { setPanel({ kind: 'log', asset }); }
  function handleSaved() { setPanel({ kind: 'none' }); setRefreshKey((k) => k + 1); }
  function handleLogged() { setPanel({ kind: 'none' }); setRefreshKey((k) => k + 1); }
  function handleClosePanel() { setPanel({ kind: 'none' }); }

  const sidebarView =
    currentView === 'library' && panel.kind === 'form' && panel.asset === null ? 'add' : currentView;

  return (
    <div className="asset-registry-scope" style={{ display: 'flex', height: 'calc(100vh - 100px)', marginTop: '64px', marginBottom: '100px', background: '#f7f9fb', overflow: 'hidden' }}>
      <AssetRegistrySidebar
        currentView={sidebarView as AssetRegistryView}
        onNavigate={handleNavigate}
        queueCount={queueCount}
      />
      <main style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {currentView === 'library' && (
          <AssetLibrary onSelectAsset={handleSelectAsset} refreshKey={refreshKey} />
        )}
        {currentView === 'channel-check' && <ChannelCheck />}
        {currentView === 'review-queue' && <ReviewQueue onCountChange={setQueueCount} />}
      </main>

      {panel.kind === 'detail' && (
        <AssetDetail
          asset={panel.asset}
          onClose={handleClosePanel}
          onEdit={() => handleEditAsset(panel.asset)}
          onLogUsage={() => handleLogUsage(panel.asset)}
        />
      )}
      {panel.kind === 'form' && (
        <AssetForm asset={panel.asset} onClose={handleClosePanel} onSaved={handleSaved} />
      )}
      {panel.kind === 'log' && (
        <LogUsageModal asset={panel.asset} onClose={handleClosePanel} onLogged={handleLogged} />
      )}
    </div>
  );
}
